import React, { useEffect, useState } from 'react';
import StepperGuest from 'components/stepperGuest/index';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { GUEST_RESERVE_SERVICE_STEP } from 'constants/reservation';
import InputTitleLabel from 'components/inputTitleLabel/index';
import Proctitle from 'components/proctitle/index';
import prefecturesList from 'constants/prefectures';
import Icons from 'images/icons';
import Linked from 'components/linked/index';
import CalendarModal from 'pages/topPage/modals/calendarModal';
import AvailabilityCalendar from 'components/availabilityCalendar';
import dayjs from 'dayjs';
import axios from 'utils/axiosUtil';
import { GUEST_SHOP_LIST_PATH, RESERVE_DETAIL_SEARCH, GUEST_SPECIFIED_SHOP_LIST_PATH } from 'constants/apiURL';
import parse from 'html-react-parser';
import InputTextGuest from 'components/inputTextGuest/index';
import CalendarDescriptionLabel from 'pages/reservation/components/calendarDescriptionLabel';
import InputSelectGuest from 'components/inputSelectGuest/index';
import Path, { ERROR_MESSAGE_TRANSITION } from 'constants/routePath';
import { messageModalActions } from 'slices/messageModalSlice';
import Title from '../../constants/title';
import { Helmet } from 'react-helmet';
import WorkDescriptionModal from '../reservation/modals/workDescriptionModal';
import RankModal from '../reservation/modals/rankModal';
import { useDeviceSize } from 'hooks/useDeviceSize';

const GuestShopAndDateSelect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [shopList, setShopList] = useState(location.state?.shopList || []);
  const [specifiedShopList, setSpecifiedShopList] = useState([]);
  const [selectedPrefecture, setSelectedPrefecture] = useState(location.state?.selectedPrefecture || null);
  const [selectedShop, setSelectedShop] = useState(location.state?.selectedShop || null);
  const [selectedShopInfo, setSelectedShopInfo] = useState(null);
  const [selectedDate, setSelectedDate] = useState(location.state?.selectedDate || null);
  const [dailyInfoList, setDailyInfoList] = useState(location.state?.dailyInfoList || []);
  const [requiredTime, setRequiredTime] = useState(location.state?.requiredTime || null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isWorkDescriptionModalOpen, setIsWorkDescriptionModalOpen] = useState(false);
  const [isRankModalOpen, setIsRankModalOpen] = useState(false);
  const todayReservation = location.state?.todayReservation || null;
  const isModify = location.state?.isModify || false;
  const today = dayjs().format('YYYY/MM/DD');
  const formattedDate = selectedDate || today;
  const token = useSelector(state => state.account.token);
  const openWorkDescriptionModal = () => setIsWorkDescriptionModalOpen(true);
  const closeWorkDescriptionModal = () => setIsWorkDescriptionModalOpen(false);
  const openRankModal = () => setIsRankModalOpen(true);
  const closeRankModal = () => setIsRankModalOpen(false);
  const { isSp } = useDeviceSize();

  const CHUBUN_CD = '021';
  const SHOBUN_CD = ['101'];
  const USER_RANK = '';
  const ENABLE_RANK = '0';

  useEffect(() => {
    fetchSpecifiedShopList();
  }, []);

  useEffect(() => {
    if (!token) {
      const message = ERROR_MESSAGE_TRANSITION;
      dispatch(messageModalActions.showMessageModal({ message }));
      navigate(Path.GUEST_RESERVATION);
      return;
    }
  }, [token, navigate, dispatch]);

  // 店舗一覧取得APIを呼び出す
  const fetchShopList = async prefecturesCode => {
    const response = await axios.get(GUEST_SHOP_LIST_PATH, {
      params: {
        kbn: "5",
        prefecturesCode,
      },
    });
    const shopList = response.body.shopList || [];
    const filteredSpecifiedShopList = specifiedShopList.filter(shop => shop.prefecturesCode === prefecturesCode);
    shopList.push(...filteredSpecifiedShopList);
    // ゲスト特定店舗を含むshopListを電話番号順の昇順でソート
    shopList.sort((a, b) => {
      const telA = String(a.tel ?? '').replace(/\D/g, '');
      const telB = String(b.tel ?? '').replace(/\D/g, '');
      return telA.localeCompare(telB, undefined, { numeric: true });
    });
    setShopList(shopList);
  };

  // ゲスト特定店舗取得APIを呼び出す
  const fetchSpecifiedShopList = async () => {
    const response = await axios.get(GUEST_SPECIFIED_SHOP_LIST_PATH);
    setSpecifiedShopList(response.body.shopList || []);
  };

  // 空き状況取得APIを呼び出す
  const fetchReserveTime = async (shopCode, date) => {
    const response = await axios.get(RESERVE_DETAIL_SEARCH, {
      params: {
        date,
        shopCode,
        chubunCd: CHUBUN_CD,
        shobunCd: SHOBUN_CD,
        userRank: USER_RANK,
        enableRank: ENABLE_RANK,
      },
    });
    setRequiredTime(response.body?.requiredTime);
    const dailyInfo = response.body?.dailyInfo || [];
    // 日付と曜日区分を含むデータを整形
    const formattedData = dailyInfo.map(info => ({
      date: info.date,
      dayKbn: parseInt(info.dayKbn, 10),
      outOfTermMessage: info.outOfTermMessage || '',
      closedFlag: info.closedFlag || '0',
      showKeyFlag: info.showKeyFlag || '0',
      hmInfo: (info.hmInfo || []).map(hm => ({
        hm: hm.hm,
        availability: hm.availability,
      })),
    }));
    setDailyInfoList(formattedData);
  };

  // 日付選択時の処理
  const handleDateSelect = date => {
    const formatted = dayjs(date).format('YYYY/MM/DD');
    setSelectedDate(formatted);
    closeCalendar();

    if (selectedShop) {
      const dateString = dayjs(formatted).format('YYYYMMDD');
      fetchReserveTime(selectedShop, dateString);
    }
  };

  // 都道府県変更時に店舗リストを取得し、都道府県が未選択の場合は店舗リストをクリア
  const handlePrefectureChange = value => {
    setSelectedPrefecture(value);
    setSelectedShop(null);
    setSelectedShopInfo(null);
    if (value) {
      fetchShopList(value);
    } else {
      setShopList([]);
    }
  };

  // 店舗変更時の処理
  const handleShopChange = value => {
    setSelectedShop(value);
    setDailyInfoList([]);
    setSelectedShopInfo(shopList.find(shop => shop.shopCode === value));
    // 店舗変更時、選択済み日付があれば予約可能時間を再検索
    if (value && selectedDate) {
      const dateString = dayjs(selectedDate).format('YYYYMMDD');
      fetchReserveTime(value, dateString);
    }
  };

  const openCalendar = () => {
    setIsCalendarOpen(true);
  };

  const closeCalendar = () => {
    setIsCalendarOpen(false);
  };

  // 独自タグで囲まれたゲスト特定店舗情報のパース
  const parseGuestSpecifiedShopInfo = (info = '') => {
    // タグの内容を抽出
    const extract = tag => {
      const match = info.match(new RegExp(`<${tag}>([\\s\\S]*?)<\\/${tag}>`));
      return match ? match[1].trim() : '';
    };
    // 営業時間は複数行を配列化
    const businessHours = extract('businessHours')
      .split('\n')
      .map(line => {
        const match = line.trim().match(/^(.+?)\s*(\d{2}:\d{2}-\d{2}:\d{2})$/);
        return match ? { label: match[1], time: match[2] } : null;
      })
      .filter(Boolean);

    return {
      description: extract('description'),
      shopName: extract('shopName'),
      phoneNumber: extract('phoneNumber'),
      address: extract('address'),
      businessHours,
      closedDay: extract('closedDay'),
      url: extract('url'),
    };
  };

  // ゲスト特定店舗情報の表示
  const renderGuestSpecifiedShopInfo = () => {
    const parsed = parseGuestSpecifiedShopInfo(selectedShopInfo?.guestSpecifiedShopInfo);

    return (
      <div className="bg-background-sub rounded max-w-screen-sm mx-4 sm:mx-auto">
        <div className="text-sm py-8 px-4 sm:px-12">
            {parsed.description && (
              <p className="whitespace-pre-wrap mb-2">{parsed.description}</p>
            )}
            {parsed.shopName && (
              <p className="font-bold">{parsed.shopName}</p>
            )}
            {parsed.phoneNumber && (
              <p className="font-bold mb-10">電話番号：{parsed.phoneNumber}</p>
            )}
            {parsed.address && (
              <div>
                <p>住所</p>
                <p>{parsed.address}</p>
              </div>
            )}
            {parsed.businessHours && parsed.businessHours.length > 0 && (
              <div className="mt-6">
                <p>営業時間</p>
                <table className="text-sm">
                  <tbody>
                    {parsed.businessHours.map((hour, idx) => (
                      <tr key={idx}>
                        <td className="pr-4 whitespace-nowrap">{hour.label}</td>
                        <td>{hour.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {parsed.closedDay && (
              <div className="mb-6">
                <table className="text-sm">
                  <tbody>
                    <tr>
                      <td className="pr-8 whitespace-nowrap">定休日</td>
                      <td>{parsed.closedDay}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            {parsed.url && (
              <div>
                <p>店舗ホームページ</p>
                <a
                  href={parsed.url}
                  className="text-link underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {parsed.url}
                </a>
                <p
                  className="mt-1"
                  style={{
                    fontSize: isSp ? '12px' : '16px',
                    color: 'rgba(0, 36, 69, 0.7)',
                    fontWeight: 500,
                  }}
                >
                  ※外部サイトへ遷移します
                </p>
              </div>
            )}
        </div>
      </div>
    );
  };

  return (
    <div className="mt-4 lg:px-20">
      <Helmet>
        <title>{Title.GUEST_RESERVATION}</title>
      </Helmet>
      <StepperGuest
        labels={GUEST_RESERVE_SERVICE_STEP}
        previousStep={location.state?.previousStep}
        activeStep={0}
        fullWidth={true}
        className={{
          stepperClass: 'max-sm:mx-8 sm:mx-36',
          bottomClass: 'h-full w-full text-left',
        }}
      />
      <div className="mt-12 sm:mt-24">
        <Proctitle
          useH1={true}
          text="STEP1 店舗・日時を選ぶ"
          className={{ textClass: 'whitespace-nowrap' }}
        />
      </div>
      <div className="mt-6 sm:mt-2">
        <div className="flex flex-col sm:flex-row sm:items-center">
          <div className="sm:mt-12 sm:w-1/3 sm:min-w-[160px]">
            <InputTitleLabel title="予約店舗" required={true} />
          </div>
          <div className="flex flex-row gap-2 sm:mt-12 sm:w-1/3 sm:min-w-[340px] sm:max-w-[420px] sm:gap-4">
            <InputSelectGuest
              fieldName={'selectedPrefecture'}
              placeholder="都道府県"
              value={selectedPrefecture}
              disabled={false}
              options={prefecturesList}
              onChange={handlePrefectureChange}
            />
            <InputSelectGuest
              fieldName={'selectedShop'}
              placeholder="店舗"
              value={selectedShop}
              disabled={!selectedPrefecture}
              options={shopList.map(shop => ({
                value: shop.shopCode,
                label: shop.shopName,
              }))}
              onChange={handleShopChange}
            />
          </div>
        </div>
        {!selectedShopInfo?.guestSpecifiedShopFlag && (
          <div className="mt-6 sm:mt-2 sm:flex sm:items-center">
            <div className="sm:mt-12 sm:w-1/3 sm:min-w-[160px]">
              <InputTitleLabel title="日時" required={true} />
            </div>
            <div className="flex w-full gap-2 sm:w-[320px] sm:gap-4">
              <InputTextGuest
                fieldName={'selectedDate'}
                placeholder={formattedDate}
                value={selectedDate}
                onClick={openCalendar}
                disabled={false}
              />
              <div className="mt-1 w-full sm:mt-12 sm:w-[280px]">
                <div className="flex h-[44px] w-[44px] items-center justify-center rounded-full bg-white">
                  <Icons.Image
                    src={Icons.cal}
                    className="h-6 w-6 cursor-pointer"
                    onClick={openCalendar}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="mt-10">
          {selectedShop && dailyInfoList.length > 0 && !selectedShopInfo?.guestSpecifiedShopFlag && (
            <CalendarDescriptionLabel className="mb-4" />
          )}
        </div>
        <div className="mt-2"
          style={{
            margin: isSp ? '0 calc(50% - 50vw) 0' : '',
            width: isSp ? '100vw' : '',
          }}
        >
          {selectedShopInfo?.guestSpecifiedShopFlag
            ? renderGuestSpecifiedShopInfo()
            : selectedShop &&
              dailyInfoList.length > 0 && (
                <AvailabilityCalendar
                  dailyInfoList={dailyInfoList}
                  rank={null}
                  isModifyTodayReservation={isModify && todayReservation}
                  className="mt-4"
                  onClickLeft={newDate => {
                    fetchReserveTime(
                      selectedShop,
                      dayjs(newDate).format('YYYYMMDD')
                    );
                  }}
                  onClickRight={newDate => {
                    fetchReserveTime(
                      selectedShop,
                      dayjs(newDate).format('YYYYMMDD')
                    );
                  }}
                  onClickCircle={hmInfo => {
                    const selectedDateFormatted = dayjs(hmInfo.date).format(
                      'YYYY/MM/DD'
                    );
                    setSelectedDate(selectedDateFormatted);
                    navigate(Path.GUEST_CAR_INFO, {
                      state: {
                        ...location.state,
                        shopList,
                        selectedPrefecture,
                        selectedShop,
                        selectedDate: selectedDateFormatted,
                        dailyInfoList,
                        requiredTime,
                        shopAndDateInfo: {
                          shop: shopList.find(
                            shop => shop.shopCode === selectedShop
                          ),
                          date: selectedDateFormatted,
                          time: hmInfo.hm,
                        },
                      },
                    });
                  }}
                  onClickLock={openRankModal}
                  onClickSquare={openWorkDescriptionModal}
                />
              )}
        </div>
      </div>
      {(dailyInfoList.length === 0 || !selectedShop) &&
        !selectedShopInfo?.guestSpecifiedShopFlag && (
          <div className="mb-4 mt-2 flex items-center justify-center bg-background-sub px-4 py-6 sm:px-10">
            <span className="text-xs">
              日付を選ぶと予約可能な時間が表示されます。
            </span>
          </div>
        )}
      <div className="mb-12 mt-12 flex items-center justify-center text-center sm:mb-24">
        <Linked
          text="戻る"
          onClick={() => {
            if (location.state?.from === 'GUEST_SEARCH') {
              navigate(Path.GUEST_INFO, { state: location.state });
            } else {
              navigate(Path.GUEST_RESERVATION);
            }
          }}
          isLine
          linkIcon="L"
          className="font-bold"
        />
      </div>
      <CalendarModal
        isModalOpen={isCalendarOpen}
        toggleModal={closeCalendar}
        onSelect={handleDateSelect}
        disabledDate={d =>
          dayjs(dayjs().format('YYYY-MM-DD')).isAfter(dayjs(d, 'day'))
        }
      />
      <RankModal
        isModalOpen={isRankModalOpen}
        closeModal={closeRankModal}
      />
      <WorkDescriptionModal
        isModalOpen={isWorkDescriptionModalOpen}
        toggleModal={closeWorkDescriptionModal}
        shopName={shopList?.find(shop => shop.shopCode === selectedShop)?.shopName}
        shopTel={shopList?.find(shop => shop.shopCode === selectedShop)?.tel}
        supportChargeFlag={shopList?.find(shop => shop.shopCode === selectedShop)?.supportChargeFlag}
        isOilWork={false}
      />
    </div>
  );
};

export default GuestShopAndDateSelect;
